<template>
  <div class="page-logic-settings">
    <div class="page-logic-settings__section">
      <h4 class="page-logic-settings__title">Navigation Rules</h4>
      <p class="page-logic-settings__description">Define where to go when this page is submitted</p>

      <div v-for="(rule, index) in pageLogic" :key="index" class="page-logic-settings__rule">
        <div class="page-logic-settings__rule-header">
          <span class="page-logic-settings__rule-title">Rule {{ index + 1 }}</span>
          <Btn
            variant="plain"
            icon="trash"
            @click="removeRule(index)"
            class="page-logic-settings__rule-delete"
          />
        </div>

        <div class="condition-groups">
          <div class="condition-groups__header">If the following conditions are met:</div>

          <template v-for="(group, groupIndex) in rule.conditions.groups.binding" :key="groupIndex">
            <ConditionGroup
              :group="group"
              :groupIndex="groupIndex"
              :items="availableElements"
              @addRule="(groupIndex) => addConditionRule(index, groupIndex)"
              @removeRule="
                (groupIndex, ruleIndex) => removeConditionRule(index, groupIndex, ruleIndex)
              "
              @addGroup="() => addConditionGroup(index)"
              @removeGroup="(groupIndex) => removeConditionGroup(index, groupIndex)"
            />

            <div v-if="rule.conditions.groups.binding.length > 0" class="condition-groups__logic">
              <template v-if="groupIndex === 0 && rule.conditions.groups.binding.length > 1">
                <SelectInput
                  id="between-group-logic"
                  :options="[
                    { label: 'And', value: 'AND' },
                    { label: 'Or', value: 'OR' },
                  ]"
                  v-model="rule.conditions.betweenGroupLogic.binding"
                  class="p-0 logic-selector"
                />
              </template>
              <template v-else-if="groupIndex < rule.conditions.groups.binding.length - 1">
                <span class="condition-groups__logic-text">
                  {{ rule.conditions.betweenGroupLogic.binding }}
                </span>
              </template>
            </div>
          </template>

          <div class="condition-groups__actions">
            <Btn
              icon="layer-plus"
              variant="tertiary"
              @click="() => addConditionGroup(index)"
              type="button"
            >
              Add group
            </Btn>
          </div>
        </div>

        <div class="page-logic-settings__destination">
          <label>Then go to:</label>
          <SelectInput
            v-model="rule.destination"
            :options="destinationOptions"
            placeholder="Select destination"
          />
        </div>
      </div>

      <Btn
        variant="secondary"
        icon="plus"
        text="Add Rule"
        @click="addRule"
        class="page-logic-settings__add-rule"
      />
    </div>

    <div class="page-logic-settings__section">
      <h4 class="page-logic-settings__title">Default Path</h4>
      <p class="page-logic-settings__description">Where to go if no conditions match</p>

      <div class="page-logic-settings__destination">
        <SelectInput
          v-model="defaultDestination"
          :options="destinationOptions"
          placeholder="Select default destination"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed } from 'vue';
  import { useFormStore } from '@/store/formStore';
  import Btn from '@/components/partials/Btn.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import RadioGroup from '@/components/inputs/RadioGroup.vue';
  import ConditionGroup from '@/components/form/settings/ConditionGroup.vue';

  export default {
    name: 'PageLogicSettings',

    components: {
      Btn,
      SelectInput,
      RadioGroup,
      ConditionGroup,
    },

    props: {
      page: {
        type: Object,
        required: true,
      },
      form: {
        type: Object,
        required: true,
      },
    },

    emits: ['update-settings'],

    setup(props, { emit }) {
      const formStore = useFormStore();

      // Initialize page logic from props or with defaults
      const pageLogic = ref(props.page.logic?.rules || []);
      const defaultDestination = ref(props.page.logic?.defaultDestination || 'next');

      // Get available elements for conditions
      const availableElements = computed(() => {
        return formStore
          .getAllElements()
          .filter((element) => element.type !== 'heading' && element.type !== 'text')
          .map((element) => ({
            id: element.id,
            label: element.props?.label || `Field ${element.id}`,
            type: element.type,
            choices: element.type.includes('choice') ? element.props?.choices : null,
          }));
      });

      // Compute available destinations (other pages + ending)
      const destinationOptions = computed(() => {
        const options = [
          { label: 'Next Page', value: 'next' },
          { label: 'End Form', value: 'end' },
        ];

        // Add other pages as options
        props.form.pages.forEach((page, index) => {
          if (page.id !== props.page.id) {
            options.push({
              label: page.title || `Page ${index + 1}`,
              value: page.id,
            });
          }
        });

        // Add ending pages as options
        if (props.form.ending_pages?.length > 0) {
          options.push({ label: '──────────', value: 'divider', disabled: true });
          props.form.ending_pages.forEach((page, index) => {
            options.push({
              label: `${page.title || `Ending ${index + 1}`}`,
              value: `ending_${page.id}`,
            });
          });
        }

        return options;
      });

      const addRule = () => {
        pageLogic.value.push({
          conditions: {
            betweenGroupLogic: {
              binding: 'AND',
              error: '',
              rules: ['required'],
            },
            groups: {
              binding: [
                {
                  logic: {
                    binding: 'AND',
                    error: '',
                    rules: ['required'],
                  },
                  rules: [
                    {
                      elementId: {
                        binding: '',
                        error: '',
                        rules: ['required'],
                      },
                      operation: {
                        binding: 'equals',
                        error: '',
                        rules: ['required'],
                      },
                      value: {
                        binding: '',
                        error: '',
                        rules: ['required'],
                      },
                    },
                  ],
                },
              ],
            },
          },
          destination: 'next',
        });
        emitUpdate();
      };

      const removeRule = (index) => {
        pageLogic.value.splice(index, 1);
        emitUpdate();
      };

      // Condition group management
      const addConditionGroup = (ruleIndex) => {
        pageLogic.value[ruleIndex].conditions.groups.binding.push({
          logic: {
            binding: 'AND',
            error: '',
            rules: ['required'],
          },
          rules: [
            {
              elementId: {
                binding: '',
                error: '',
                rules: ['required'],
              },
              operation: {
                binding: 'equals',
                error: '',
                rules: ['required'],
              },
              value: {
                binding: '',
                error: '',
                rules: ['required'],
              },
            },
          ],
        });
        emitUpdate();
      };

      const removeConditionGroup = (ruleIndex, groupIndex) => {
        pageLogic.value[ruleIndex].conditions.groups.binding.splice(groupIndex, 1);
        emitUpdate();
      };

      const addConditionRule = (ruleIndex, groupIndex) => {
        pageLogic.value[ruleIndex].conditions.groups.binding[groupIndex].rules.push({
          elementId: {
            binding: '',
            error: '',
            rules: ['required'],
          },
          operation: {
            binding: 'equals',
            error: '',
            rules: ['required'],
          },
          value: {
            binding: '',
            error: '',
            rules: ['required'],
          },
        });
        emitUpdate();
      };

      const removeConditionRule = (ruleIndex, groupIndex, conditionRuleIndex) => {
        pageLogic.value[ruleIndex].conditions.groups.binding[groupIndex].rules.splice(
          conditionRuleIndex,
          1
        );
        emitUpdate();
      };

      const emitUpdate = () => {
        emit('update-settings', {
          logic: {
            rules: pageLogic.value,
            defaultDestination: defaultDestination.value,
          },
        });
      };

      return {
        pageLogic,
        defaultDestination,
        destinationOptions,
        availableElements,
        addRule,
        removeRule,
        addConditionGroup,
        removeConditionGroup,
        addConditionRule,
        removeConditionRule,
      };
    },
  };
</script>

<style scoped lang="scss">
  .page-logic-settings {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    &__section {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__title {
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-900);
      margin: 0;
    }

    &__description {
      font-size: 0.875rem;
      color: var(--slate-600);
      margin: 0;
    }

    &__rule {
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      padding: 1rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__rule-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__rule-title {
      font-weight: 500;
      color: var(--slate-700);
    }

    &__destination {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      label {
        font-size: 0.875rem;
        color: var(--slate-700);
      }
    }

    &__add-rule {
      align-self: flex-start;
    }

    .condition-groups {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 1rem;
      background-color: var(--slate-50);
      border-radius: 0.5rem;

      &__header {
        font-weight: 500;
        color: var(--slate-700);
        margin-bottom: 1rem;
      }

      &__logic {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        margin: 0.5rem 0;
      }

      &__logic-text {
        font-weight: 500;
        color: var(--slate-700);
      }

      &__actions {
        margin-top: 0.5rem;
        display: flex;
        justify-content: flex-start;
      }
    }
  }
</style>
